import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  AddWidgetIcon,
  AnalysisIcon,
  CloseIcon,
  MaximizeIcon,
  MinimizeIcon
} from "@/assets/icons";
import Tooltip from "@/components/ui/tooltip";
import { MESSAGE } from "@/constants/message";
import { useWindowSize } from "@/hooks/ui/useWindowSize";
import { RootState } from "@/store";
import {
  getWidgetGridBreakpoint,
  getWidgetSectionLayout,
  groupWidgetsBySection
} from "@/utils/widget";

import { twMerge } from "tailwind-merge";

import { Button } from "../../ui/button";
import DatasetWidgetCard from "./DatasetWidgetCard";
import WidgetConfiguration from "./widgetConfiguration";

interface DatasetWidgetPopupProps {
  onToggleDatasetWidget: () => void;
}

export default function DatasetWidgetPopup({
  onToggleDatasetWidget
}: Readonly<DatasetWidgetPopupProps>) {
  const [showWidgetConfigModal, setShowWidgetConfigModal] = useState(false);
  // True when the user opens the configuration modal from an existing widget
  // card; false when opening via the "Add Widget" button. Drives the chart-type
  // tab lock inside the modal.
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { width } = useWindowSize();
  const breakpoint = useMemo(() => getWidgetGridBreakpoint(width), [width]);

  const { widgets, visualizationOrder } = useSelector(
    (state: RootState) => state.widget
  );

  const orderedWidget = useMemo(
    () => visualizationOrder.map((id) => widgets[id]).filter(Boolean),
    [visualizationOrder, widgets]
  );

  const widgetSections = useMemo(
    () => groupWidgetsBySection(orderedWidget),
    [orderedWidget]
  );

  const renderWidgetCard = (widget: (typeof orderedWidget)[number]) => (
    <DatasetWidgetCard
      key={widget.widget_id}
      name={widget.label}
      widget={widget}
      onOpenWidgetConfig={() => {
        setShowWidgetConfigModal(true);
        setIsEditing(true);
      }}
    />
  );

  return (
    <div
      className={twMerge(
        "absolute top-[30px] right-20 border-gainsboro bg-white rounded-lg w-[450px] max-h-[calc(100vh-54px)] overflow-scroll p-4",
        isExpanded
          ? "h-[calc(100%-24px)] w-[calc(100%-24px)] !left-3 !right-3 !top-3 z-[999]"
          : ""
      )}
    >
      {/* TOP */}
      <div className="flex justify-between items-center">
        <p className="font-semibold text-xl text-txtHeading">Summary</p>
        <div className="flex gap-2 justify-center items-center">
          {orderedWidget.length > 0 && (
            <Tooltip content="Create Widget">
              <button
                onClick={() => {
                  setShowWidgetConfigModal(!showWidgetConfigModal);
                  setIsEditing(false);
                }}
              >
                <AddWidgetIcon
                  className={
                    showWidgetConfigModal ? "fill-teal-500" : "fill-dim-gray"
                  }
                  title="add widget icon"
                />
              </button>
            </Tooltip>
          )}
          <Button
            variant="ghost"
            className="size-auto [&_svg]:size-6"
            size="icon"
            aria-label={
              isExpanded ? "Minimize Widget Panel" : "Maximize Widget Panel"
            }
            data-testid="summary-maximize-button"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <MinimizeIcon className="fill-dim-gray" />
            ) : (
              <MaximizeIcon className="fill-dim-gray" />
            )}
          </Button>
          <button onClick={() => onToggleDatasetWidget()}>
            <CloseIcon />
          </button>
        </div>
      </div>
      {/* CONTENT */}
      {isExpanded ? (
        <div className="flex flex-col gap-4 mt-4">
          {widgetSections.map((section, sectionIndex) => {
            const { cols, emptySlots } = getWidgetSectionLayout(
              section,
              breakpoint
            );

            return (
              <div
                key={`${section.type}-${sectionIndex}`}
                data-testid={`widget-grid-section-${section.type}`}
                className="grid gap-4"
                style={{
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`
                }}
              >
                {section.widgets.map(renderWidgetCard)}
                {Array.from({ length: emptySlots }, (_, i) => (
                  <div key={`empty-${i}`} data-testid="widget-empty-slot" />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-3 mt-4">
          {orderedWidget.map(renderWidgetCard)}
        </div>
      )}
      {/* EMPTY CONTENT */}
      {orderedWidget.length === 0 && (
        <div className="flex flex-col gap-2 justify-center items-center mt-10 h-[calc(100vh-200px)] px-12">
          <AnalysisIcon className="w-14 h-14 fill-light-gray" />
          <p className="text-txtPlaceholder text-[16px] text-center">
            {MESSAGE.DATASET_WIDGET_NO_SUPPORTED_DATASET_DESCRIPTION}
          </p>
        </div>
      )}
      <WidgetConfiguration
        open={showWidgetConfigModal}
        onClose={() => setShowWidgetConfigModal(false)}
        isEditing={isEditing}
      />
    </div>
  );
}
